import { FilteredComponentSystemUpdateEntityArgs } from '@common/systems/SystemBase';

import { ClientRegistry } from '../components';
import { ClientFilteredComponentSystem } from './ClientBase';

/**
 * Updates active WeaponSwing entities on the client.
 * Mirrors server animation logic for snappy visual feedback.
 */
export class ClientWeaponSwingUpdateSystem extends ClientFilteredComponentSystem {
  public componentClasses: (keyof ClientRegistry)[] = ['WeaponSwing', 'Transform', 'IsPredicted'];

  updateEntity({
    world,
    entityId,
    deltaTime,
  }: FilteredComponentSystemUpdateEntityArgs<ClientRegistry>): void {
    const swing = world.getComponent(entityId, 'WeaponSwing')!;
    const transform = world.getComponent(entityId, 'Transform')!;
    const attackerId = swing.ownerEntityId;

    // Update progress
    const elapsedMs = swing.elapsedMs + deltaTime * 1000;
    const progress = Math.min(1, elapsedMs / swing.durationMs);

    this.updateComponent(entityId, swing, {
      elapsedMs,
      progress,
    });

    if (progress >= 1) {
      // Cleanup visual-only entity (server will handle authoritative removal)
      const childOf = world.getComponent(entityId, 'ChildOf');
      if (childOf) {
        world.removeChildren(childOf.parent, [entityId]);
      }
      world.destroyEntity(entityId);
      return;
    }

    // progress: 0.0 (start) -> 1.0 (end of swing)
    // Animation logic (must match server)
    const attackerFacing = world.getComponent(attackerId, 'Facing');
    if (!attackerFacing) {
      // If parent is lost (e.g. disconnected or out of AOI), stop animating
      return;
    }

    const weaponStats = this.clientServices.common.combat.getWeaponStats(
      world,
      this.clientServices.equipment,
      attackerId,
    );

    const attackerHitbox = world.getComponent(attackerId, 'Hitbox');
    const { x: centerX, y: centerY } = attackerHitbox
      ? this.clientServices.common.combat.getHitboxCenter(attackerHitbox)
      : { x: 0, y: -16 };

    const { x: localX, y: localY } = this.clientServices.common.combat.getArcSwingOffset(
      progress,
      swing.hitboxIndex,
      weaponStats.hitboxCount,
      swing.maxExtension,
      weaponStats.arc,
      attackerFacing.direction,
    );

    this.updateComponent(entityId, transform, {
      x: centerX + localX,
      y: centerY + localY,
    });
  }
}
